/**
 * @file     RevertChip.tsx
 * @purpose  One-tap undo for a manual search edit the AI flagged as harmful.
 *           "Restore" applies the previous set to the live map/list; "Keep
 *           mine" dismisses. An offer, never an auto-revert.
 */
'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { reltaTrack } from '@/hooks/useReltaTrack';
import type { RevertSuggestion } from '@/services/ai-chat/types';

export function RevertChip({ suggestion, onRestore }: { suggestion: RevertSuggestion; onRestore: () => void }) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [done, setDone] = useState<null | 'restored' | 'kept'>(null);

  if (done) {
    return (
      <Typography sx={{ fontSize: 12.5, color: theme.semanticColors.textMuted, mt: 1 }}>
        {done === 'restored' ? t('aiChat.revertRestored', 'Restored.') : t('aiChat.revertKept', 'Kept your change.')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1, px: 1.5, py: 1,
        borderRadius: 1,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        backgroundColor: theme.semanticColors.surfaceCard,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.semanticColors.textPrimary, flex: 1, minWidth: 150 }}>
        {suggestion.summary}
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={() => { onRestore(); setDone('restored'); reltaTrack('ai_chat.revert_restored', {}); }}
        sx={{ borderRadius: 1, textTransform: 'none', fontSize: 13 }}
      >
        {t('aiChat.revertRestore', 'Restore')}
      </Button>
      <Button
        variant="text"
        size="small"
        onClick={() => { setDone('kept'); reltaTrack('ai_chat.revert_dismissed', {}); }}
        sx={{ borderRadius: 1, textTransform: 'none', fontSize: 12.5, color: theme.semanticColors.textSecondary }}
      >
        {t('aiChat.revertKeep', 'Keep mine')}
      </Button>
    </Box>
  );
}
